/**
 * Operation Management - Table
 *
 * Custom table matching Figma design 573:28047
 * RTL layout: columns ordered right-to-left visually
 */

import { useState, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Typography, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuClock, LuCheck, LuChevronDown, LuPencil } from 'react-icons/lu';
import { ToastMessageContext } from '@context/toast-message-cotnext';
import { useUpdateMaintenanceTask } from '../services/operations.mutations';
import { MAINTENANCE_TASKS_LIST_URL } from '../constants';
import type { IMaintenanceTaskListItem } from '../types';

interface IOperationTableProps {
  items: IMaintenanceTaskListItem[];
  totalCount: number;
  isLoading: boolean;
  activePage: number;
  perPage: string;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: string) => void;
  onEdit: (item: IMaintenanceTaskListItem) => void;
}

// --- Priority badge colors (matching Figma) ---
const getPriorityStyle = (priority: string) => {
  const lower = priority?.toLowerCase();
  if (lower === 'emergency' || lower === 'طارئة' || lower === 'طارئ') return { backgroundColor: '#FEE2E2', color: '#991B1B', borderColor: '#FECACA' };
  if (lower === 'urgent' || lower === 'عاجل' || lower === 'عالية' || lower === 'high') return { backgroundColor: '#FEE2E2', color: '#DC2626', borderColor: '#FECACA' };
  if (lower === 'routine' || lower === 'روتيني' || lower === 'متوسطة' || lower === 'medium') return { backgroundColor: '#EDE9FE', color: '#7C3AED', borderColor: '#DDD6FE' };
  if (lower === 'low' || lower === 'منخفضة' || lower === 'منخفض') return { backgroundColor: '#DBEAFE', color: '#2563EB', borderColor: '#BFDBFE' };
  return { backgroundColor: '#F3F4F6', color: '#6B7280', borderColor: '#E5E7EB' };
};

// --- Status badge colors — matching API enums: awaiting_processing, assigned, in_progress, completed, canceled ---
const getStatusStyle = (status: string) => {
  const lower = status?.toLowerCase();
  if (lower === 'awaiting_processing' || lower === 'pending' || lower === 'في الإنتظار' || lower === 'في الانتظار' || lower === 'قيد الانتظار' || lower === 'waiting')
    return { backgroundColor: '#F3F4F6', color: '#6B7280' };
  if (lower === 'assigned' || lower === 'scheduled' || lower === 'مجدولة')
    return { backgroundColor: '#DBEAFE', color: '#2563EB' };
  if (lower === 'in_progress' || lower === 'inprogress' || lower === 'in progress' || lower === 'قيد التنفيذ' || lower === 'في التنفيذ')
    return { backgroundColor: '#EDE9FE', color: '#7C3AED' };
  if (lower === 'completed' || lower === 'done' || lower === 'منتهي' || lower === 'مكتملة' || lower === 'مكتمل')
    return { backgroundColor: '#D1FAE5', color: '#059669' };
  if (lower === 'canceled' || lower === 'cancelled' || lower === 'ملغية' || lower === 'ملغا')
    return { backgroundColor: '#FEE2E2', color: '#DC2626' };
  return { backgroundColor: '#F3F4F6', color: '#6B7280' };
};

// Format date: "MM/DD"
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}`;
  } catch {
    return dateStr;
  }
};

// Format time from ISO: "ص 08:00" or "م 02:00"
const formatTime = (dateStr: string | null): string => {
  if (!dateStr) return '';
  try {
    let hours: number;
    let minutes: number;
    if (dateStr.includes('T')) {
      const timePart = dateStr.split('T')[1];
      const hm = timePart.split(':');
      hours = parseInt(hm[0], 10);
      minutes = parseInt(hm[1], 10);
    } else if (dateStr.includes(':')) {
      const parts = dateStr.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
    } else {
      return '';
    }
    if (isNaN(hours) || isNaN(minutes)) return '';
    const period = hours < 12 ? 'ص' : 'م';
    const h12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${period} ${String(h12).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  } catch {
    return '';
  }
};

// --- Table Header ---
const TableHeader = () => {
  const { t } = useTranslation();

  const columns = [
    { label: t('OperationManagement.colTaskType'), flex: '1 1 120px' },
    { label: t('OperationManagement.colProperty'), flex: '1 1 140px' },
    { label: t('OperationManagement.colAssignee'), flex: '1 1 140px' },
    { label: t('OperationManagement.colTaskTiming'), flex: '1 1 140px' },
    { label: t('OperationManagement.colPriority'), flex: '0 0 100px' },
    { label: t('OperationManagement.colTaskStatus'), flex: '0 0 130px' },
    { label: '', flex: '0 0 40px' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: '12px',
        py: '10px',
        borderBottom: '1px solid #F2F2F2',
      }}
    >
      {columns.map((col, i) => (
        <Box key={i} sx={{ flex: col.flex, minWidth: 0 }}>
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', fontWeight: 500, color: '#8E98A1', textAlign: 'start' }}>
            {col.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// --- Status options for inline dropdown (values match API enums) ---
const STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'awaiting_processing', labelKey: 'OperationManagement.statusWaiting' },
  { value: 'assigned', labelKey: 'OperationManagement.statusScheduled' },
  { value: 'in_progress', labelKey: 'OperationManagement.statusInProgress' },
  { value: 'completed', labelKey: 'OperationManagement.statusDone' },
  { value: 'canceled', labelKey: 'OperationManagement.statusCancelled' },
];

// --- Table Row ---
const TableRow = ({ item, onEdit }: { item: IMaintenanceTaskListItem; onEdit: (item: IMaintenanceTaskListItem) => void }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);
  const queryClient = useQueryClient();
  const updateMutation = useUpdateMaintenanceTask();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const isUpdating = updateMutation.isPending || isRefetching;

  const priorityStyle = getPriorityStyle(item.taskPriority);
  const statusStyle = getStatusStyle(item.taskStatus);
  const priorityLabel = isRtl ? (item.taskPriorityAr || item.taskPriority) : (item.taskPriorityEn || item.taskPriority);
  const statusLabel = isRtl ? (item.taskStatusAr || item.taskStatus) : (item.taskStatusEn || item.taskStatus);
  const taskTypeLabel = isRtl ? (item.maintenanceTypeAr || item.maintenanceType) : (item.maintenanceTypeEn || item.maintenanceType);

  const handleStatusClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!isUpdating) setAnchorEl(e.currentTarget);
  };

  const handleStatusChange = async (newStatus: string) => {
    setAnchorEl(null);
    if (newStatus === item.taskStatus) return;
    if (!item.maintenanceRequestId) return;
    try {
      const response = await updateMutation.mutateAsync({
        requestId: item.maintenanceRequestId,
        body: {
          taskDueAt: item.taskDateTime || '',
          assignee: item.serviceProviderId || '',
          taskStatus: newStatus,
          priority: item.taskPriority || '',
        },
      });
      if (response.isSuccess) {
        showSuccess(t('OperationManagement.taskUpdatedSuccess'));
        setIsRefetching(true);
        await queryClient.invalidateQueries({ queryKey: [MAINTENANCE_TASKS_LIST_URL] });
        setIsRefetching(false);
      } else {
        const msg = isRtl
          ? response.errorMessages?.[0]?.messageAr
          : response.errorMessages?.[0]?.messageEn;
        showError(msg || t('ERRORS.SOMETHING_WENT_WRONG'));
      }
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: '12px',
        py: '12px',
        borderBottom: '1px solid #F2F2F2',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#FAFAFA' },
      }}
      onClick={() => onEdit(item)}
    >
      {/* Task Type */}
      <Box sx={{ flex: '1 1 120px', minWidth: 0 }}>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#435363', textAlign: 'start' }}>
          {taskTypeLabel}
        </Typography>
      </Box>

      {/* Property */}
      <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#435363', textAlign: 'start' }}>
          {item.propertyName || '—'}
        </Typography>
      </Box>

      {/* Assignee */}
      <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#435363', textAlign: 'start' }}>
          {item.serviceProviderName || '—'}
        </Typography>
      </Box>

      {/* Task Timing (date + time) */}
      <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#435363' }}>
            {formatDate(item.taskDateTime)}
          </Typography>
          {item.taskDateTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <LuClock size={12} color="#8E98A1" />
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#8E98A1' }}>
                {formatTime(item.taskDateTime)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Priority — pill badge */}
      <Box sx={{ flex: '0 0 100px', minWidth: 0 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '12px',
            py: '4px',
            borderRadius: '100px',
            backgroundColor: priorityStyle.backgroundColor,
            border: `1px solid ${priorityStyle.borderColor}`,
          }}
        >
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', fontWeight: 500, color: priorityStyle.color }}>
            {priorityLabel}
          </Typography>
        </Box>
      </Box>

      {/* Task Status — clickable dropdown */}
      <Box sx={{ flex: '0 0 130px', minWidth: 0 }}>
        <Box
          onClick={handleStatusClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            cursor: isUpdating ? 'wait' : 'pointer',
            opacity: isUpdating ? 0.6 : 1,
            borderRadius: '6px',
            px: '4px',
            py: '2px',
            '&:hover': { backgroundColor: isUpdating ? 'transparent' : 'rgba(0,0,0,0.04)' },
          }}
        >
          {isUpdating ? (
            <CircularProgress size={14} sx={{ color: statusStyle.color }} />
          ) : null}
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: statusStyle.color, fontWeight: 500 }}>
            {statusLabel}
          </Typography>
          <LuChevronDown size={14} color={statusStyle.color} />
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={(e: React.SyntheticEvent) => { e.stopPropagation?.(); setAnchorEl(null); }}
          onClick={(e) => e.stopPropagation()}
          slotProps={{ paper: { sx: { borderRadius: '12px', mt: '4px', minWidth: '160px' } } }}
        >
          {STATUS_OPTIONS.map((opt) => {
            const optStyle = getStatusStyle(opt.value);
            const isActive = opt.value === item.taskStatus;
            return (
              <MenuItem
                key={opt.value}
                onClick={() => handleStatusChange(opt.value)}
                selected={isActive}
                sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: optStyle.color, gap: '8px' }}
              >
                {isActive ? <LuCheck size={14} color={optStyle.color} /> : null}
                {t(opt.labelKey)}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>

      {/* Edit icon — pen in subtle circle */}
      <Box sx={{ flex: '0 0 40px', display: 'flex', justifyContent: 'center' }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(item);
          }}
          sx={{
            color: '#8E98A1',
            width: '32px',
            height: '32px',
            border: '1px solid #E9E9E9',
            borderRadius: '50%',
          }}
        >
          <LuPencil size={14} />
        </IconButton>
      </Box>
    </Box>
  );
};

// --- Pagination ---
const Pagination = ({
  totalCount,
  activePage,
  perPage,
  onPageChange,
  onPerPageChange,
}: {
  totalCount: number;
  activePage: number;
  perPage: string;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: string) => void;
}) => {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / Number(perPage)));

  const pages: (number | '...')[] = [];
  for (let i = 1; i <= Math.min(totalPages, 5); i++) pages.push(i);
  if (totalPages > 5) {
    pages.push('...');
    pages.push(totalPages);
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: '16px' }}>
      {/* Per page selector — Figma: on the left side (start in RTL) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            border: '1px solid #E9E9E9',
            borderRadius: '8px',
            px: '8px',
            height: '32px',
            cursor: 'pointer',
          }}
        >
          <select
            value={perPage}
            onChange={(e) => onPerPageChange(e.target.value)}
            style={{
              border: 'none',
              background: 'transparent',
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '14px',
              color: '#8E98A1',
              cursor: 'pointer',
              outline: 'none',
              direction: 'rtl',
            }}
          >
            {['4', '10', '20', '50'].map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </Box>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#8E98A1' }}>
          {t('OperationManagement.resultsPerPage')}
        </Typography>
      </Box>

      {/* Page numbers — Figma: on the right side (end in RTL) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* Next (right arrow — appears on far right in RTL) */}
        <Box
          onClick={() => activePage < totalPages && onPageChange(activePage + 1)}
          sx={{
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '5px',
            backgroundColor: '#E9E9E9',
            cursor: activePage < totalPages ? 'pointer' : 'default',
            opacity: activePage < totalPages ? 1 : 0.5,
            boxShadow: '0px 0px 6px rgba(0,0,0,0.02), 0px 2px 4px rgba(0,0,0,0.08)',
          }}
        >
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#435363', lineHeight: 1 }}>&lt;</Typography>
        </Box>

        {pages.map((p, i) => (
          <Box
            key={i}
            onClick={() => typeof p === 'number' && onPageChange(p)}
            sx={{
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '5px',
              backgroundColor: p === activePage ? '#5B3FFF' : '#FFFFFF',
              cursor: typeof p === 'number' ? 'pointer' : 'default',
              boxShadow: '0px 0px 6px rgba(0,0,0,0.02), 0px 2px 4px rgba(0,0,0,0.08)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontSize: '14px',
                color: p === activePage ? '#FFFFFF' : '#5B3FFF',
                lineHeight: 1,
              }}
            >
              {p}
            </Typography>
          </Box>
        ))}

        {/* Previous (left arrow — appears on far left in RTL = last in visual order) */}
        <Box
          onClick={() => activePage > 1 && onPageChange(activePage - 1)}
          sx={{
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '5px',
            backgroundColor: '#EEEBFF',
            cursor: activePage > 1 ? 'pointer' : 'default',
            opacity: activePage > 1 ? 1 : 0.5,
            boxShadow: '0px 0px 6px rgba(0,0,0,0.02), 0px 2px 4px rgba(0,0,0,0.08)',
          }}
        >
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#5B3FFF', lineHeight: 1 }}>&gt;</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// --- Main Table Component ---
const OperationTable = ({
  items,
  totalCount,
  isLoading,
  activePage,
  perPage,
  onPageChange,
  onPerPageChange,
  onEdit,
}: IOperationTableProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ width: '100%' }}>
      <TableHeader />

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <div
            className="animate-spin rounded-full h-8 w-8"
            style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }}
          />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && items.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '16px', color: '#8E98A1' }}>
            {t('OperationManagement.noTasks')}
          </Typography>
        </Box>
      )}

      {/* Rows */}
      {!isLoading &&
        items.map((item, i) => (
          <TableRow key={item.maintenanceRequestId || i} item={item} onEdit={onEdit} />
        ))}

      {/* Pagination */}
      <Pagination
        totalCount={totalCount}
        activePage={activePage}
        perPage={perPage}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
      />
    </Box>
  );
};

export default OperationTable;
